import React, { useMemo, useState } from 'react';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs, { Dayjs } from 'dayjs';

import { Button } from '../../../../components/core';
import { DatePicker } from '../../../Paylinks/components/DatePicker';

export type DateRangePreset = '7d' | '30d' | 'custom';

export type DateRangeValue = {
  preset: DateRangePreset;
  from: Dayjs;
  to: Dayjs;
};

type DateRangeSelectorProps = {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
};

/** Inclusive calendar-day limit for custom ranges (matches the 30D preset). */
export const MAX_CUSTOM_RANGE_DAYS = 30;

const PRESETS: { id: DateRangePreset; label: string }[] = [
  { id: '7d', label: '7D' },
  { id: '30d', label: '30D' },
  { id: 'custom', label: 'CUSTOM' },
];

const isWithinMaxRange = (from: Dayjs, to: Dayjs): boolean =>
  to.startOf('day').diff(from.startOf('day'), 'day') < MAX_CUSTOM_RANGE_DAYS;

export const resolvePresetRange = (preset: Exclude<DateRangePreset, 'custom'>): DateRangeValue => {
  const to = dayjs().endOf('day');
  if (preset === '7d') {
    return { preset, from: dayjs().subtract(6, 'day').startOf('day'), to };
  }
  return { preset, from: dayjs().subtract(29, 'day').startOf('day'), to };
};

export const DateRangeSelector = ({ value, onChange }: DateRangeSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftStart, setDraftStart] = useState<Dayjs | null>(value.from);
  const [draftEnd, setDraftEnd] = useState<Dayjs | null>(value.to);

  const isCustomOpen = Boolean(anchorEl);
  const showCustomChip = value.preset === 'custom';

  const chipLabel = useMemo(
    () => `${value.from.format('MM/DD/YYYY')} - ${value.to.format('MM/DD/YYYY')}`,
    [value.from, value.to],
  );

  const today = dayjs().endOf('day');
  const startMinDate = draftEnd ? draftEnd.subtract(MAX_CUSTOM_RANGE_DAYS - 1, 'day') : undefined;
  const startMaxDate = draftEnd && draftEnd.isBefore(today) ? draftEnd : today;
  const endMinDate = draftStart ?? undefined;
  const startPlusMax = draftStart
    ? draftStart.add(MAX_CUSTOM_RANGE_DAYS - 1, 'day').endOf('day')
    : undefined;
  const endMaxDate = startPlusMax && startPlusMax.isBefore(today) ? startPlusMax : today;

  const canApply =
    !!draftStart &&
    !!draftEnd &&
    !draftEnd.isBefore(draftStart) &&
    isWithinMaxRange(draftStart, draftEnd);

  const handlePresetClick = (preset: DateRangePreset, event: React.MouseEvent<HTMLElement>) => {
    if (preset === 'custom') {
      setDraftStart(value.from);
      setDraftEnd(value.to);
      setAnchorEl(event.currentTarget.parentElement);
      return;
    }
    setAnchorEl(null);
    onChange(resolvePresetRange(preset));
  };

  const handleApplyCustom = () => {
    if (!draftStart || !draftEnd || !isWithinMaxRange(draftStart, draftEnd)) {
      return;
    }
    const from = draftStart.startOf('day');
    const to = draftEnd.endOf('day');
    if (to.isBefore(from)) {
      return;
    }
    onChange({ preset: 'custom', from, to });
    setAnchorEl(null);
  };

  const handleClearCustom = () => {
    setAnchorEl(null);
    onChange(resolvePresetRange('7d'));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: 200,
          height: 32,
          borderRadius: '8px',
          backgroundColor: 'secondary.light',
          p: '0 4px',
        }}
        data-testid="ella-date-range-selector"
      >
        {PRESETS.map((preset) => {
          const selected =
            preset.id === 'custom'
              ? value.preset === 'custom' || isCustomOpen
              : value.preset === preset.id;
          return (
            <Box
              key={preset.id}
              component="button"
              type="button"
              onClick={(e) => handlePresetClick(preset.id, e)}
              aria-pressed={selected}
              data-testid={`ella-date-range-${preset.id}`}
              sx={{
                flex: 1,
                height: selected ? 32 : 26,
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: selected ? 'primary.dark' : 'transparent',
                color: selected ? 'common.white' : 'text.primary',
                fontFamily: 'Poppins, sans-serif',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
                px: 0.5,
              }}
            >
              {preset.label}
            </Box>
          );
        })}
      </Box>

      {showCustomChip && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.5,
            py: 0.5,
            borderRadius: '4px',
            backgroundColor: 'secondary.light',
          }}
          data-testid="ella-custom-date-chip"
        >
          <Typography variant="small" sx={{ px: 0.5 }}>
            {chipLabel}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label="Clear custom date range"
            onClick={handleClearCustom}
            sx={{
              display: 'inline-flex',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              p: 0,
              color: 'text.secondary',
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </Box>
        </Box>
      )}

      <Popper
        open={isCustomOpen}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              p: 1.25,
              width: 300,
              borderRadius: '8px',
            }}
            data-testid="ella-custom-date-popover"
          >
            <Typography variant="small" color="text.secondary" sx={{ mb: 0.75 }}>
              Custom date range (max {MAX_CUSTOM_RANGE_DAYS} days)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.25 }}>
              <DatePicker
                label="Start date"
                dateOnly
                disablePast={false}
                disableFuture
                value={draftStart}
                onChange={(next) => setDraftStart(next)}
                minDate={startMinDate}
                maxDate={startMaxDate}
                slotProps={{
                  textField: { size: 'small' },
                  popper: { placement: 'bottom-start' },
                }}
              />
              <DatePicker
                label="End date"
                dateOnly
                disablePast={false}
                disableFuture
                value={draftEnd}
                onChange={(next) => setDraftEnd(next)}
                minDate={endMinDate}
                maxDate={endMaxDate}
                slotProps={{
                  textField: { size: 'small' },
                  popper: { placement: 'bottom-start' },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, '& button': { height: 40 } }}>
              <Button
                theme="secondary"
                text="Cancel"
                isFullWidth
                onClick={() => setAnchorEl(null)}
                dataTestId="ella-custom-date-cancel"
              />
              <Button
                theme="primary"
                text="Apply"
                isFullWidth
                onClick={handleApplyCustom}
                disabled={!canApply}
                dataTestId="ella-custom-date-apply"
              />
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
